import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, FormControl, Nav } from 'react-bootstrap';
import { ArrowLeft, XCircle } from 'react-bootstrap-icons';
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getSafeReturnPath, makeApiRequests } from '../../helpers/api';
import { ENDPOINTS } from '../../helpers/constants';
import {
  conversationBankSearchPath,
  readConversationBankSession,
  writeConversationBankSession
} from '../../helpers/conversationBank';
import Loader from '../Loader';
import ConversationBankCard from './ConversationBankCard';
import './conversationBank.scss';

const useQuery = () => {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
};

const ConversationBankSearch = () => {
  const history = useHistory();
  const queryParams = useQuery();
  const contactId = queryParams.get('contactId') || '';
  const returnTo = getSafeReturnPath(queryParams.get('returnTo')) || (contactId ? `/crm/${contactId}` : '');

  const saved = readConversationBankSession();
  const [query, setQuery] = useState(saved.query || '');
  const [topicCategory, setTopicCategory] = useState(saved.topicCategory || '');
  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState([]);
  const [recent, setRecent] = useState([]);
  const [frequent, setFrequent] = useState([]);
  const [categories, setCategories] = useState([]);
  const [total, setTotal] = useState(0);
  const [placeholderContext, setPlaceholderContext] = useState({});
  const [lead, setLead] = useState(null);

  const persist = useCallback(
    patch => {
      writeConversationBankSession({
        query,
        topicCategory,
        responseKind: '',
        contactId,
        returnTo,
        ...patch
      });
    },
    [query, topicCategory, contactId, returnTo]
  );

  const fetchResults = useCallback(async () => {
    setLoading(true);
    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.CONVERSATION_BANK_SEARCH,
      requestBody: {
        query,
        topicCategory,
        contactId,
        mode: 'sales'
      }
    });
    setLoading(false);
    if (error) {
      toast.error(error);
      return;
    }
    setResults(response.results || []);
    setRecent(response.recent || []);
    setFrequent(response.frequent || []);
    setCategories(response.categories || []);
    setTotal(response.total || 0);
    setPlaceholderContext(response.placeholderContext || {});
    setLead(response.lead || null);
  }, [query, topicCategory, contactId]);

  useEffect(() => {
    persist({ query, topicCategory });
    const handle = setTimeout(fetchResults, 250);
    return () => clearTimeout(handle);
  }, [fetchResults, persist, query, topicCategory]);

  const trackUsage = async ({ eventType, record, branch, responseType }) => {
    await makeApiRequests({
      endpoint: ENDPOINTS.CONVERSATION_BANK_USAGE,
      requestBody: {
        eventType,
        record,
        branch: branch || undefined,
        responseType,
        contactId: contactId || undefined
      }
    });
  };

  const openFlow = result => {
    persist({ lastRecordId: result._id, lastBranchId: result.matchingBranchId || '' });
    trackUsage({ eventType: 'opened', record: result._id, branch: result.matchingBranchId });
    const path = conversationBankSearchPath({
      contactId,
      returnTo,
      id: result._id
    });
    history.push(result.matchingBranchId ? `${path}#branch-${result.matchingBranchId}` : path);
  };

  const resetFilters = () => {
    setQuery('');
    setTopicCategory('');
  };

  const hasFilters = Boolean(query || topicCategory);

  return (
    <div className="cb-page px-3 pb-5">
      {lead && (
        <div className="cb-lead-banner rounded-border p-3 mt-3 mb-2">
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <div className="tinyFont text-muted text-uppercase">Attached lead</div>
              <div className="font-weight-bold">{lead.buyer}</div>
              <div className="smallFont text-muted">{lead.status}</div>
            </div>
            {returnTo && (
              <Button size="sm" variant="outline-dark" onClick={() => history.push(returnTo)}>
                Return to lead
              </Button>
            )}
          </div>
          <div className="tinyFont text-muted mt-2 mb-0">
            Copy the language, then paste it into your message. Nothing is sent automatically.
          </div>
        </div>
      )}

      <div className="cb-search-sticky">
        <FormControl
          className="cb-search-input"
          placeholder="Search the words the customer used"
          value={query}
          onChange={e => setQuery(e.target.value)}
          autoComplete="off"
        />
        <Nav
          variant="tabs"
          className="cb-sales-tabs mt-2"
          activeKey={topicCategory || 'all'}
          onSelect={key => {
            if (key === null || key === undefined) return;
            setTopicCategory(key === 'all' ? '' : key);
          }}
        >
          <Nav.Item>
            <Nav.Link eventKey="all">All</Nav.Link>
          </Nav.Item>
          {categories.map(category => (
            <Nav.Item key={category}>
              <Nav.Link eventKey={category}>{category}</Nav.Link>
            </Nav.Item>
          ))}
        </Nav>
        <div className="d-flex justify-content-between align-items-center mt-2">
          <div className="smallFont text-muted">
            {loading ? 'Searching…' : `${total} result${total === 1 ? '' : 's'}`}
          </div>
          {hasFilters && (
            <Button variant="link" className="p-0 smallFont" onClick={resetFilters}>
              <XCircle className="mr-1" />
              Reset filters
            </Button>
          )}
        </div>
      </div>

      {loading && (
        <div className="mt-3">
          <Loader />
        </div>
      )}

      {!loading && !hasFilters && (recent.length > 0 || frequent.length > 0) && (
        <>
          {recent.length > 0 && (
            <div className="mt-3">
              <h6 className="mb-2">Recently used</h6>
              {recent.map(result => (
                <ConversationBankCard
                  key={`recent-${result._id}`}
                  result={result}
                  placeholderContext={placeholderContext}
                  onOpen={openFlow}
                  onCopied={meta => trackUsage({ eventType: 'copied', ...meta })}
                />
              ))}
            </div>
          )}
          {frequent.length > 0 && (
            <div className="mt-3">
              <h6 className="mb-2">Frequently used</h6>
              {frequent.map(result => (
                <ConversationBankCard
                  key={`freq-${result._id}`}
                  result={result}
                  placeholderContext={placeholderContext}
                  onOpen={openFlow}
                  onCopied={meta => trackUsage({ eventType: 'copied', ...meta })}
                />
              ))}
            </div>
          )}
          <h6 className="mt-4 mb-2">All conversations</h6>
        </>
      )}

      {!loading && results.length === 0 && (
        <Alert variant="light" className="mt-3 border">
          No matching conversations. Try a shorter phrase such as “how much”, “bad credit”, or “family land”.
        </Alert>
      )}

      {!loading &&
        results
          .filter(result => {
            if (hasFilters) return true;
            const recentIds = new Set(recent.map(item => String(item._id)));
            const frequentIds = new Set(frequent.map(item => String(item._id)));
            return !recentIds.has(String(result._id)) && !frequentIds.has(String(result._id));
          })
          .map(result => (
          <ConversationBankCard
            key={`${result._id}-${result.flowPosition}-${result.matchingBranchId || 'start'}`}
            result={result}
            placeholderContext={placeholderContext}
            onOpen={openFlow}
            onCopied={meta => trackUsage({ eventType: 'copied', ...meta })}
          />
        ))}

      {returnTo && !lead && (
        <Button variant="link" className="px-0 mt-2" onClick={() => history.push(returnTo)}>
          <ArrowLeft className="mr-1" />
          Back
        </Button>
      )}
    </div>
  );
};

export default ConversationBankSearch;
